import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, X, Plus, Trash2, Package, Users } from "lucide-react";
import { toast } from "sonner";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

export default function FavoritesPage() {
  const { user } = useAuth();
  const [lists, setLists] = useState([]);
  const [activeList, setActiveList] = useState(null);
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState(""); // ""=all
  const [loading, setLoading] = useState(true);
  const [newListName, setNewListName] = useState("");
  useDocumentTitle("Favorites");

  const loadLists = async () => {
    try {
      const { data } = await api.get("/favorites/lists");
      setLists(data || []);
      if (!activeList && data?.length) setActiveList(null); // "All" default
    } catch (_) { /* ignore */ }
  };
  const loadItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeList) params.list_id = activeList;
      if (kind) params.kind = kind;
      const { data } = await api.get("/favorites", { params });
      setItems(data || []);
    } catch (_) { setItems([]); } finally { setLoading(false); }
  };

  useEffect(() => { if (user) loadLists(); }, [user?.id]);
  useEffect(() => { if (user) loadItems(); /* eslint-disable-next-line */ }, [user?.id, activeList, kind]);

  const createList = async (e) => {
    e?.preventDefault();
    if (!newListName.trim()) return;
    try {
      const { data } = await api.post("/favorites/lists", { name: newListName.trim() });
      setNewListName("");
      setLists((prev) => [...prev, data]);
      toast.success("List created");
    } catch (err) { toast.error(err?.response?.data?.error || "Could not create list"); }
  };

  const removeList = async (id) => {
    if (!window.confirm("Delete this list? Items will move back to your general favorites.")) return;
    try {
      await api.delete(`/favorites/lists/${id}`);
      setLists((prev) => prev.filter((l) => l.id !== id));
      if (activeList === id) setActiveList(null);
      toast.success("List removed");
    } catch (err) { toast.error(err?.response?.data?.error || "Could not delete"); }
  };

  const remove = async (fav) => {
    try {
      await api.delete(`/favorites/${fav.id}`);
      setItems((prev) => prev.filter((x) => x.id !== fav.id));
    } catch (err) { toast.error("Could not remove"); }
  };

  return (
    <DashboardShell>
      <div className="max-w-6xl">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Favorites</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Your saved pros and services.</h1>
            <p className="text-[#252A34]/70 text-sm mt-1">Organize into lists so you can rebook fast next time.</p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-12">
          {/* Sidebar: lists */}
          <aside className="lg:col-span-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid="fav-lists">
              <button
                onClick={() => setActiveList(null)}
                className={`w-full text-left px-3 py-2 rounded-xl text-sm ${!activeList ? "bg-[#165a71]/10 text-[#071b22] font-medium" : "text-[#252A34]/80 hover:bg-slate-50"}`}
                data-testid="fav-list-all"
              >
                All favorites
              </button>
              {lists.map((l) => (
                <div key={l.id} className="flex items-center group">
                  <button
                    onClick={() => setActiveList(l.id)}
                    className={`flex-1 text-left px-3 py-2 rounded-xl text-sm truncate ${activeList === l.id ? "bg-[#165a71]/10 text-[#071b22] font-medium" : "text-[#252A34]/80 hover:bg-slate-50"}`}
                    data-testid={`fav-list-${l.id}`}
                  >
                    {l.name}
                  </button>
                  {!l.is_default && (
                    <button onClick={() => removeList(l.id)} className="opacity-0 group-hover:opacity-100 rounded p-1.5 text-[#252A34]/60 hover:text-rose-600" aria-label="Delete list" data-testid={`fav-list-remove-${l.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
              <form onSubmit={createList} className="mt-2 flex gap-1.5">
                <input
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="New list…"
                  className="flex-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs"
                  data-testid="fav-new-list-input"
                />
                <button type="submit" className="rounded-lg bg-[#165a71] text-white px-2.5 py-1.5 text-xs" data-testid="fav-new-list-btn">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </form>
            </div>
          </aside>

          <div className="lg:col-span-9">
            <div className="mb-4 inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="fav-kind-filter">
              {[
                { k: "", label: "All", icon: Heart },
                { k: "provider", label: "Pros", icon: Users },
                { k: "service", label: "Services", icon: Package },
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setKind(t.k)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full ${kind === t.k ? "bg-[#165a71] text-white" : "text-[#252A34]/80"}`}
                  data-testid={`fav-kind-${t.k || "all"}`}
                >
                  <t.icon className="h-3.5 w-3.5" /> {t.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="text-[#252A34]/60">Loading…</div>
            ) : items.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
                <Heart className="h-6 w-6 mx-auto text-rose-500" />
                <div className="mt-2 font-display text-lg text-[#071b22] font-semibold">Save your first favorite.</div>
                <div className="text-sm text-[#252A34]/70 mt-1">Tap the heart on any provider or service to add it here.</div>
                <Link to="/providers" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium">Browse pros</Link>
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="fav-items">
                {items.map((fav) => (
                  <FavCard key={fav.id} fav={fav} onRemove={() => remove(fav)} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}

function FavCard({ fav, onRemove }) {
  const t = fav.target || {};
  const to = fav.kind === "provider" ? `/providers/${t.slug || fav.target_id}` : `/services/${t.slug || fav.target_id}`;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-[#165a71]/40 group relative" data-testid={`fav-card-${fav.id}`}>
      <button
        onClick={onRemove}
        className="absolute top-3 right-3 rounded-full bg-white border border-slate-200 p-1 text-[#252A34]/60 hover:text-rose-600 opacity-80 group-hover:opacity-100"
        aria-label="Remove"
        data-testid={`fav-remove-${fav.id}`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <Link to={to} className="flex items-start gap-3">
        <div className="h-14 w-14 rounded-xl bg-[#165a71]/10 flex items-center justify-center text-[#165a71] shrink-0 overflow-hidden">
          {t.profile_image || t.hero_image || t.featured_image ? (
            <img src={t.profile_image || t.hero_image || t.featured_image} alt="" className="w-full h-full object-cover" />
          ) : fav.kind === "provider" ? <Users className="h-5 w-5" /> : <Package className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-[#252A34]/60">{fav.kind}</div>
          <div className="font-display font-semibold text-[#071b22] mt-0.5 truncate">
            {t.business_name || t.display_name || t.title || "—"}
          </div>
          {fav.kind === "provider" && (t.rating_avg !== undefined) && (
            <div className="text-xs text-[#252A34]/60 mt-1">★ {(t.rating_avg || 0).toFixed(1)} ({t.rating_count || 0})</div>
          )}
          {fav.kind === "service" && t.base_price && (
            <div className="text-xs text-[#252A34]/60 mt-1">From ${t.base_price}</div>
          )}
        </div>
      </Link>
    </div>
  );
}
